import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Stack from '@mui/material/Stack';
import moment from 'moment';

// Global enterprise styling dictionary configuring standard outlined filter tracks
const enterpriseFiltersInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.01)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-root': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const Filters = ({ visible, meta, setMeta }) => {
    const invoice = useSelector(state => state.invoice.invoiceData);
    const [formValues, setFormValues] = useState(meta)
    const [filterButtonDisabled, setFilterButtonDisabled] = useState(true)
    const [startDate, setStartDate] = React.useState(null);
    const [endDate, setEndDate] = React.useState(null);
    const [checkFormUse, setCheckFormUse] = useState("")

    const submitFilterValues = () => {
        setMeta(prevState => ({
            ...prevState,
            [`start_date`]: formValues["start_date"],
            [`end_date`]: formValues["end_date"],
            [`page`]: 1
        }));
        setCheckFormUse(true)
    }

    const onDateChange = (date_type, date) => {
        if(date != "" && typeof date !== "undefined"){
           let date_formatted = (date != null) ? moment(date).format('YYYY-MM-DD') : date
           if(date_type == "start_date"){
            setStartDate(date_formatted)
            setFormValues({
                ...formValues,
                [`start_date`]: date_formatted
            });
           }else{
            setEndDate(date_formatted)
            setFormValues({
                ...formValues,
                [`end_date`]: date_formatted
            });
           }
        }
    }

    const handleKeyUp = (event) => {
        if (event.keyCode === 13) {
            submitFilterValues();
        }
    }

    const resetFields = () => {
        setStartDate(null)
        setEndDate(null)
        setFormValues({
            ...formValues,
            [`start_date`]: "",
            [`end_date`]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            [`start_date`]: "",
            [`end_date`]: "",
            [`page`]: 1,
        }));
    }

    useEffect(() => {
        setCheckFormUse("")
        setFilterButtonDisabled(checkFormHasValues())
    }, [formValues])

    const checkFormHasValues = () => {
        let disableButton = true
        if (formValues["start_date"] !== ""  || formValues["end_date"] !== "") {
            disableButton = false
        }
        return disableButton
    }

    useEffect(() => {
        if ((formValues["start_date"] !== ""  || formValues["end_date"] !== "") && checkFormUse==='') {
            setFormValues({
                ...formValues,
                [`start_date`]: "",
                [`end_date`]: "",
            });
            setMeta(prevState => ({
                ...prevState,
                [`start_date`]: "",
                [`end_date`]: "",
                [`page`]: 1,
            }));
            setStartDate(null)
            setEndDate(null)
        }
    }, [invoice])

    return (
        <>
            {visible &&
                <Box 
                    className="filtercontainer"
                    sx={{
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box 
                        className="filteraccordion" 
                        onKeyUp={handleKeyUp}
                        sx={{ width: '100%' }}
                    >
                        <Grid container spacing={2.5} alignItems="center">
                            
                            {/* 1. Date Select Input Controls Matrix Container Group */}
                            <Grid item xs={12} md={7} lg={8}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Grid container spacing={2}>
                                        
                                        {/* Start Date Picker Input Field */}
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth variant="outlined" size="small">
                                                <DatePicker
                                                    label="Start Date"
                                                    value={startDate}
                                                    disableFuture={true}
                                                    inputFormat={"yyyy-MM-dd"}
                                                    onChange={(newValue) => {
                                                        onDateChange("start_date", newValue);
                                                    }}
                                                    // CRASH RESOLVED: Intercepts components compilation with legacy/v5 slots API pattern using raw unmodified variables
                                                    slots={{
                                                        textField: (params) => (
                                                            <TextField 
                                                                {...params} 
                                                                label="Start Date"
                                                                variant="outlined" 
                                                                size="small" 
                                                                sx={enterpriseFiltersInputStyles} 
                                                            />
                                                        )
                                                    }}
                                                    // Dynamic secondary rendering mapping intercept protection layer
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="Start Date"
                                                            variant="outlined" 
                                                            size="small" 
                                                            sx={enterpriseFiltersInputStyles} 
                                                        />
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>

                                        {/* End Date Picker Input Field */}
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth variant="outlined" size="small">
                                                <DatePicker
                                                    label="End Date"
                                                    value={endDate}
                                                    disableFuture={true}
                                                    inputFormat={"yyyy-MM-dd"}
                                                    onChange={(newValue) => {
                                                        onDateChange("end_date", newValue);
                                                    }}
                                                    // CRASH RESOLVED: Intercepts components compilation with legacy/v5 slots API pattern using raw unmodified variables
                                                    slots={{
                                                        textField: (params) => (
                                                            <TextField 
                                                                {...params} 
                                                                label="End Date"
                                                                variant="outlined" 
                                                                size="small" 
                                                                sx={enterpriseFiltersInputStyles} 
                                                            />
                                                        )
                                                    }}
                                                    // Dynamic secondary rendering mapping intercept protection layer
                                                    renderInput={(params) => (
                                                        <TextField 
                                                            {...params} 
                                                            label="End Date"
                                                            variant="outlined" 
                                                            size="small" 
                                                            sx={enterpriseFiltersInputStyles} 
                                                        />
                                                    )}
                                                />
                                            </FormControl>
                                        </Grid>

                                    </Grid>
                                </LocalizationProvider>
                            </Grid>

                            {/* 2. Operational Action Controls Module Bar */}
                            <Grid item xs={12} md={5} lg={4}>
                                <Box 
                                    className="filterfooter"
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' }, 
                                        gap: 1.5,
                                        width: '100%',
                                        height: '40px'
                                    }}
                                >
                                    <Button 
                                        variant="outlined" 
                                        onClick={resetFields} 
                                        disabled={filterButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            height: '38px',
                                            px: 3,
                                            borderColor: '#E2E8F0',
                                            color: '#475569',
                                            '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC', color: '#1E293B' },
                                            '&.Mui-disabled': { borderColor: '#E2E8F0', color: '#CBD5E1' }
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={submitFilterValues} 
                                        disabled={filterButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            height: '38px',
                                            px: 4,
                                            boxShadow: 'none',
                                            backgroundColor: '#1976d2',
                                            color: '#FFFFFF',
                                            '&:hover': { backgroundColor: '#115293', boxShadow: 'none' },
                                            '&.Mui-disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                                        }}
                                    >
                                        Filter
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>
                </Box>
            }
        </>
    )
}

export default Filters;